import useSWR from 'swr';
import { useMemo } from 'react';
// utils
import { fetcher, put, endpoints } from 'src/utils/api-client';
// hooks
import { useThemeOptOut } from 'src/hooks/use-theme-opt-out';
// theme registry
import { getCustomTheme } from 'src/theme/custom-themes/registry';

// ----------------------------------------------------------------------

const URL = endpoints.appConfig.activeTheme;

// How the header image is displayed:
// - 'contain': whole image at its original aspect ratio (default)
// - 'cover': fills/crops a fixed-height banner
export const HEADER_FIT = { CONTAIN: 'contain', COVER: 'cover' };
const DEFAULT_HEADER_FIT = HEADER_FIT.CONTAIN;

// Header layout:
// - 'banner': image is a card inside the page (default)
// - 'background': fixed image behind the menu header + content, page scrolls over it
export const HEADER_LAYOUT = { BANNER: 'banner', BACKGROUND: 'background' };
const DEFAULT_HEADER_LAYOUT = HEADER_LAYOUT.BANNER;

// Celebration effect (confetti + ribbons).
const DEFAULT_CELEBRATION_ENABLED = false;
const DEFAULT_CELEBRATION_DURATION_MS = 5000;

/**
 * Reads the active global theme + display options. Resolves the stored id
 * against the frontend registry, so unknown/disabled themes safely yield
 * `theme: null`.
 *
 * Theme changes apply on reload (data is fetched once on mount and not
 * revalidated automatically) — matching the desired behavior.
 */
export function useActiveTheme() {
  const { data, isLoading, error, mutate } = useSWR(URL, fetcher, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  });

  return useMemo(() => {
    const themeId = data?.theme || null;
    const headerFit = data?.header_fit === HEADER_FIT.COVER ? HEADER_FIT.COVER : DEFAULT_HEADER_FIT;
    const headerLayout =
      data?.header_layout === HEADER_LAYOUT.BACKGROUND
        ? HEADER_LAYOUT.BACKGROUND
        : DEFAULT_HEADER_LAYOUT;
    return {
      themeId,
      theme: getCustomTheme(themeId),
      headerFit,
      headerLayout,
      celebrationEnabled: Boolean(data?.celebration_enabled ?? DEFAULT_CELEBRATION_ENABLED),
      celebrationDurationMs: Number(data?.celebration_duration_ms) || DEFAULT_CELEBRATION_DURATION_MS,
      isLoading,
      error,
      refetch: mutate,
    };
  }, [
    data?.theme,
    data?.header_fit,
    data?.header_layout,
    data?.celebration_enabled,
    data?.celebration_duration_ms,
    isLoading,
    error,
    mutate,
  ]);
}

/**
 * Theme to actually render for the current user: the global active theme,
 * unless the user opted out of the special theme (then it's neutralized).
 * Themes flagged `forced` in the registry ignore the opt-out and always apply.
 * Use this in rendering components; use `useActiveTheme` for the admin editor.
 */
export function useAppliedTheme() {
  const config = useActiveTheme();
  const { optedOut } = useThemeOptOut();

  return useMemo(() => {
    if (!optedOut || config.theme?.forced) return config;
    return {
      ...config,
      themeId: null,
      theme: null,
      headerLayout: HEADER_LAYOUT.BANNER,
      celebrationEnabled: false,
    };
  }, [config, optedOut]);
}

/**
 * Sets (or clears, when `themeId` is null) the active global theme.
 * `headerFit` controls how the header image is displayed; `headerLayout`
 * controls whether it's a card banner or a fixed background.
 * Admin/superuser only — enforced by the backend.
 */
export async function setActiveTheme(
  themeId,
  {
    headerFit = DEFAULT_HEADER_FIT,
    headerLayout = DEFAULT_HEADER_LAYOUT,
    celebrationEnabled = DEFAULT_CELEBRATION_ENABLED,
    celebrationDurationMs = DEFAULT_CELEBRATION_DURATION_MS,
  } = {}
) {
  return put(URL, {
    theme: themeId || null,
    header_fit: headerFit,
    header_layout: headerLayout,
    celebration_enabled: celebrationEnabled,
    celebration_duration_ms: celebrationDurationMs,
  });
}
